namespace $.$$ {

	type $shm_projects_version_record = $shm_projects_data_version
	type $shm_projects_benchmark_value = $shm_projects_data_benchmark['values'][ number ]
	type $shm_projects_benchmark_row = {
		step: string
		operation: string
		values: Map< string, $shm_projects_benchmark_value >
	}
	type $shm_projects_benchmark_line = {
		id: string
		title: string
		scenario: string
		step: string
		metric: string
		unit: string
	}

	export class $shm_projects_page extends $.$shm_projects_page {

		@ $mol_mem
		override draft_note( next?: string ) {
			return this.$.$shm_projects_draft_note( this.project_id(), next )
		}

		auto() {
			return [
				... super.auto(),
				...( this.version_run_id() ? [ this.version_run_events().native() ] : [] ),
			]
		}

		project_record() {
			const project = this.$.$shm_projects_data.detail( this.project_id() )
			if( !project ) throw new Error( `Unknown project: ${ this.project_id() }` )
			return project
		}

		title() {
			return this.project_record().title
		}

		@ $mol_mem
		project_title_draft( next?: string ) {
			$mol_wire_solid()
			return next ?? this.title()
		}

		@ $mol_action
		override project_title_commit( next?: Event ) {
			if( next === undefined ) return null
			const previous = this.title()
			const title = this.project_title_draft().trim()
			if( !title ) this.project_title_draft( previous )
			else {
				if( title !== previous ) {
					const renamed = $mol_wire_sync( $shm_projects_goal_api ).project_rename( this.project_id(), title )
					this.$.$shm_projects_data.records_sync( renamed.projects )
					this.$.$shm_projects_data.detail_sync( renamed.project )
				}
				this.project_title_draft( title )
			}
			return next
		}

		@ $mol_action
		override project_title_keydown( next?: KeyboardEvent ) {
			if( next?.key !== 'Escape' ) return next ?? null
			next.preventDefault()
			this.project_title_draft( this.title() )
			this.Menu_title().focused( false )
			return next
		}

		menu_title_rows() {
			return [
				this.Menu_title(),
				this.Project_actions(),
			]
		}

		@ $mol_action
		override project_delete( next?: Event ) {
			if( next === undefined ) return null
			const deleted = $mol_wire_sync( $shm_projects_goal_api ).project_delete( this.project_id() )
			this.$.$shm_projects_data.records_sync( deleted.projects )
			this.$.$mol_state_arg.value( 'project', null )
			this.$.$mol_state_arg.value( 'project_tab', null )
			this.$.$mol_state_arg.value( 'project_task', null )
			return next
		}

		section() {
			let section = this.$.$mol_state_arg.value( 'project_tab' )
			if( !section || section === 'development' || section === 'preview' ) section = this.$.$mol_state_arg.value( 'project_tab', 'tasks' ) || 'tasks'
			if( this.project_record().directory_pending && !this.pending_sections().includes( section ) ) return 'tasks'
			return section
		}

		pending_sections() {
			return [ 'product', 'tasks', 'sessions', 'draft' ]
		}

		section_arg( section: string ) {
			return { project_tab: section }
		}

		menu_rows() {
			if( this.project_record().directory_pending ) return [
				this.Version_label(),
				... this.pending_sections().map( section => this.Section_link( section ) ),
			]
			return [
				this.Version_label(),
				... [ 'product', 'tasks', 'sessions', 'docs', 'tests', 'modules', 'dependencies', 'history', 'configuration', 'draft' ]
					.map( section => this.Section_link( section ) ),
			]
		}

		section_title( section: string ) {
			return {
				product: 'Продукт',
				configuration: 'Конфигурация',
				tasks: 'Задачи',
				sessions: 'Сессии',
				draft: 'Черновик',
				docs: 'Документация',
				modules: 'Модули',
				dependencies: 'Зависимости',
				tests: 'Тесты',
				history: 'История версий',
			}[ section ] ?? section
		}

		@ $mol_mem
		override product_description( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.description ?? ''
		}

		@ $mol_mem
		override product_problem( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.problem ?? ''
		}

		@ $mol_mem
		override product_audience( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.audience ?? ''
		}

		@ $mol_mem
		override product_value( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.value_proposition ?? ''
		}

		@ $mol_mem
		override product_scenarios( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.scenarios ?? ''
		}

		@ $mol_mem
		override product_success( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.success_criteria ?? ''
		}

		@ $mol_mem
		override product_stage( next?: string ) {
			if( next !== undefined ) return next
			return this.project_record().product?.stage ?? 'idea'
		}

		@ $mol_action
		override product_save( next?: Event ) {
			if( next === undefined ) return null
			const saved = $mol_wire_sync( $shm_projects_goal_api ).project_product_update( this.project_id(), {
				description: this.product_description(),
				problem: this.product_problem(),
				audience: this.product_audience(),
				value_proposition: this.product_value(),
				scenarios: this.product_scenarios(),
				success_criteria: this.product_success(),
				stage: this.product_stage() as $shm_projects_data_product['stage'],
			} )
			this.$.$shm_projects_data.records_sync( saved.projects )
			this.$.$shm_projects_data.detail_sync( saved.project )
			const product = saved.project.product
			if( product ) {
				this.product_description( product.description )
				this.product_problem( product.problem )
				this.product_audience( product.audience )
				this.product_value( product.value_proposition )
				this.product_scenarios( product.scenarios )
				this.product_success( product.success_criteria )
				this.product_stage( product.stage )
			}
			return next
		}

		project_pages() {
			const menu = this.Menu_page()
			switch( this.section() ) {
				case 'product': return [ menu, this.Product_page() ]
				case 'configuration': return [ menu, this.Configuration() ]
				case 'tasks': return [ menu, ... this.Tasks().pages_deep() ]
				case 'sessions': {
					const sessions = this.Sessions()
					sessions.session_rows()
					return [ menu, ... sessions.pages_deep() ]
				}
				case 'docs': {
					const docs = this.Docs()
					docs.mode_text()
					return [ menu, docs ]
				}
				case 'modules': return [
					menu,
					this.Modules_page(),
					...( this.module_selected() ? [ this.Module_page() ] : [] ),
				]
				case 'dependencies': return [
					menu,
					this.Dependencies_page(),
					...( this.dependency_selected() ? [ this.Dependency_page() ] : [] ),
				]
				case 'draft': return [ menu, this.Draft_page() ]
				case 'tests': return [ menu, ... this.Project_tests().pages_deep() ]
				case 'history': {
					const selected = this.version_selected()
					if( selected?.run_id ) this.version_run_data( selected.run_id )
					return [
						menu,
						this.History_page(),
						...( selected ? [ this.Version_page() ] : [] ),
					]
				}
				default: return [ menu, this.Docs() ]
			}
		}

		version_stages() {
			return this.version_selected()?.stages ?? []
		}

		version_stage_rows() {
			return this.version_stages().map( ( _, index )=> this.Release_stage( `history:${ index }` ) )
		}

		version_stages_section_rows() {
			return this.version_stages().length
				? [ this.Version_stages_title(), this.Version_stage_list() ]
				: []
		}

		release_stage( id: string ) {
			const [ , position ] = id.split( ':' )
			return this.version_stages()[ Number( position ) ]
		}

		release_stage_title( id: string ) {
			return this.release_stage( id )?.title ?? ''
		}

		release_stage_status( id: string ) {
			return this.release_stage( id )?.status ?? 'done'
		}

		release_stage_elapsed( id: string ) {
			const stage = this.release_stage( id )
			if( !stage ) return ''
			const elapsed = stage.status === 'running'
				? Math.max( stage.elapsed_ms, this.$.$mol_state_time.now( 1_000 ) - stage.started_at )
				: stage.elapsed_ms
			return this.duration_short( elapsed )
		}

		duration_short( elapsed: number ) {
			if( elapsed < 1_000 ) return `${ Math.max( 0, Math.round( elapsed ) ) } мс`
			const seconds = Math.floor( elapsed / 1_000 )
			if( seconds < 60 ) return `${ seconds } с`
			return `${ Math.floor( seconds / 60 ) }:${ String( seconds % 60 ).padStart( 2, '0' ) }`
		}

		version_current( next?: string ) {
			return this.$.$shm_projects_data.version( this.project_id(), next )
		}

		version_date( next?: string ) {
			return this.$.$shm_projects_data.version_date( this.project_id(), next )
		}

		version_label() {
			const date = this.version_date()
			return `Текущая версия v${ this.version_current() }${ date ? ` (${ date })` : '' }`
		}

		docs_readme_uri() {
			return this.$.$shm_projects_config.resource_url(
				this.project_record().readme,
				true,
				'base',
			)
		}

		modules() { return this.project_record().modules ?? [] }

		module_rows() {
			return this.modules().length
				? this.modules().map( module => this.Module_link( module.id ) )
				: [ this.Module_empty() ]
		}

		module_arg( id: string ) {
			return { project_tab: 'modules', project_module: id }
		}

		module_title( id: string ) { return id }

		module_selected_id() { return this.$.$mol_state_arg.value( 'project_module' ) || '' }

		module_selected() {
			return this.modules().find( module => module.id === this.module_selected_id() ) ?? null
		}

		module_selected_title() { return this.module_selected()?.id ?? 'Модуль' }

		module_readme_text() {
			const readme = this.module_selected()?.readme
			if( !readme ) return 'README для этого модуля отсутствует.'
			return this.$.$mol_fetch.text( this.$.$shm_projects_config.resource_url( readme, true, 'base' ) )
		}

		dependencies() { return this.project_record().dependencies ?? [] }

		dependency_rows() {
			return this.dependencies().length
				? this.dependencies().map( dependency => this.Dependency_link( dependency.repository ) )
				: [ this.Dependency_empty() ]
		}

		dependency_arg( repository: string ) {
			return { project_tab: 'dependencies', project_dependency: repository }
		}

		dependency_title( repository: string ) {
			return this.dependencies().find( dependency => dependency.repository === repository )?.title ?? repository
		}

		dependency_selected() {
			const repository = this.$.$mol_state_arg.value( 'project_dependency' ) || ''
			return this.dependencies().find( dependency => dependency.repository === repository ) ?? null
		}

		dependency_selected_title() { return this.dependency_selected()?.title ?? 'Зависимость' }

		dependency_status() {
			const dependency = this.dependency_selected()
			if( !dependency ) return null
			return $mol_wire_sync( this.$.$shm_projects_goal_api ).dependency_refresh(
				this.project_id(),
				dependency.repository,
			)
		}

		dependency_commits_text() {
			const status = this.dependency_status()
			if( !status ) return ''
			const commit = ( sha: string )=> status.url
				? `[${ sha.slice( 0, 12 ) }](${ status.url }/commit/${ sha })`
				: sha.slice( 0, 12 )
			return [
				`Ветка: origin/${ status.branch }`,
				`Наш коммит: ${ commit( status.local ) }`,
				`Последний коммит: ${ commit( status.latest ) }`,
			].join( '\n\n' )
		}
		dependency_behind_text() {
			const status = this.dependency_status()
			if( !status ) return ''
			if( status.behind === null ) return 'Отставание не удалось определить: локальный коммит отсутствует в истории удалённой ветки.'
			const count = ( value: number )=> `${ value } ${ value % 10 === 1 && value % 100 !== 11 ? 'коммит' :
				value % 10 >= 2 && value % 10 <= 4 && ( value % 100 < 12 || value % 100 > 14 ) ? 'коммита' : 'коммитов' }`
			const behind = status.behind ? `Отстаём на ${ count( status.behind ) }` : 'Не отстаём'
			return status.ahead ? `${ behind }; локально впереди на ${ count( status.ahead ) }` : behind
		}

		@ $mol_mem
		docs_readme_text() {
			return this.$.$mol_fetch.text( this.docs_readme_uri() )
		}

		docs_agents_uri() {
			return this.$.$shm_projects_config.resource_url(
				this.project_record().agents || 'shm/AGENTS.md',
				true,
				'base',
			)
		}

		@ $mol_mem
		docs_agents_text() {
			return this.$.$mol_fetch.text( this.docs_agents_uri() )
		}

		version_history( next?: readonly $shm_projects_version_record[] ) {
			const stored = this.$.$shm_projects_data.version_history( this.project_id(), next )
			return stored
		}

		version_records() {
			return this.version_history()
		}

		version_record( id: string ) {
			return this.version_records_display().find( record => record.id === id ) ?? null
		}

		version_records_display() {
			return this.$.$shm_projects_version_sort( this.version_records() )
		}

		version_rows() {
			return this.version_records_display().map( record => this.Version_link( record.id ) )
		}

		version_title( id: string ) {
			const record = this.version_record( id )
			if( !record ) return id
			return `v${ record.version } · ${ record.stage }${ record.date && record.date !== '-' ? ` · ${ record.date }` : '' }`
		}

		version_arg( id: string ) {
			return { project_tab: 'history', project_version: id, project_version_output: 'result' }
		}

		version_uri( id: string ) {
			return this.$.$mol_state_arg.link( this.version_arg( id ) )
		}

		version_link_current( id: string ) {
			return this.version_selected_id() === id
		}

		version_selected_id() {
			const selected = this.$.$mol_state_arg.value( 'project_version' ) ?? ''
			if( selected && this.version_record( selected ) ) return selected
			return this.version_records_display()[0]?.id ?? ''
		}

		version_selected() {
			return this.version_record( this.version_selected_id() )
		}

		version_selected_title() {
			const record = this.version_selected()
			return record ? `v${ record.version }` : 'Версия'
		}

		@ $mol_mem
		version_mode( next?: string ) {
			return this.$.$mol_state_arg.value( 'project_version_output', next ) ?? 'result'
		}

		version_result_arg() {
			return { project_version_output: 'result' }
		}

		version_benchmarks_arg() {
			return { project_version_output: 'benchmarks' }
		}

		version_session_arg( id: string ) {
			return { project_version_output: `session:${ id }` }
		}

		version_result_current() {
			return this.version_mode() === 'result'
		}

		version_benchmarks_current() {
			return this.version_mode() === 'benchmarks'
		}

		version_session_current( id: string ) {
			const mode = this.version_mode()
			if( mode === `session:${ id }` ) return true
			return mode === 'session' && this.version_session_keys()[0] === id
		}

		version_mode_rows() {
			return [
				this.Version_result_mode(),
				...( this.version_selected()?.stage === 'Релиз' ? [ this.Version_changes_mode() ] : [] ),
				this.Version_benchmarks_mode(),
				... this.version_session_keys().map( id => this.Version_session_mode( id ) ),
			]
		}

		version_content() {
			if( this.version_changes_current() ) return [ this.Version_changes() ]
			if( this.version_session_selected() ) return [ this.Version_session() ]
			if( this.version_mode() === 'benchmarks' ) return [ this.Version_benchmarks_panel() ]
			return [ this.Version_details() ]
		}

		version_changes_current() {
			return this.version_selected()?.stage === 'Релиз' && this.version_mode() === 'changes'
		}

		version_release_notes() {
			return this.version_selected()?.release_notes ?? null
		}

		version_selected_date() {
			const record = this.version_selected()
			if( !record ) return ''
			return [
				record.stage,
				...( record.date && record.date !== '-' ? [ record.date ] : [] ),
				...( this.version_selected_elapsed() ? [ this.version_selected_elapsed() ] : [] ),
			].join( ' · ' )
		}

		version_selected_elapsed() {
			const record = this.version_selected()
			if( !record || record.stage !== 'Пререлиз' ) return ''
			const run = this.version_run_data( record.run_id ?? '' )
			const prerelease = run?.prerelease
			if( !run || !prerelease ) return ''
			const timed = run.timings
				? run.timings.prerelease_ms + run.timings.deployment_ms
				: 0
			const deployment = run.deployments.find( item =>
				item.stage === 'Пререлиз' && item.version === record.version
			)
			const completed = deployment?.finalized_at
				?? deployment?.deployed_at
				?? prerelease.completed_at
			const elapsed = timed || ( completed ? completed - prerelease.started_at : 0 )
			if( elapsed <= 0 ) return ''
			const seconds = Math.floor( elapsed / 1000 )
			return `Время: ${ Math.floor( seconds / 60 ) }:${ String( seconds % 60 ).padStart( 2, '0' ) }`
		}

		version_run_id() {
			if( this.section() !== 'history' ) return ''
			return this.version_selected()?.run_id ?? ''
		}

		version_session_run() {
			return this.version_run_data( this.version_run_id() )
		}

		version_session_version() {
			return this.version_selected()?.version ?? ''
		}

		version_session_keys() {
			const run = this.version_session_run()
			if( !run ) return [] as string[]
			return [
				...( run.prerelease ? [ 'prerelease' ] : [] ),
				... Array.from( { length: run.iteration }, ( _, index )=> `iteration:${ index + 1 }` ),
			]
		}

		version_session_selected() {
			const keys = this.version_session_keys()
			if( !keys.length ) return ''
			const mode = this.version_mode()
			if( mode === 'session' ) return keys[0]
			if( !mode.startsWith( 'session:' ) ) return ''
			const selected = mode.slice( 'session:'.length )
			return keys.includes( selected ) ? selected : ''
		}

		version_session_title( id: string ) {
			return id === 'prerelease' ? 'Сессия пререлиза' : `Сессия ${ this.version_session_iteration( id ) }`
		}

		version_session_rows() {
			const run = this.version_session_run()
			const selected = this.version_session_selected()
			if( !run || !selected ) return []
			return [ this.Version_session_chat( selected ).Process_view() ]
		}

		version_session_iteration( id: string ) {
			return id === 'prerelease' ? 0 : Number( id.slice( id.indexOf( ':' ) + 1 ) )
		}

		version_session_prerelease( id: string ) {
			return id === 'prerelease'
		}

		@ $mol_mem_key
		version_run_data( id: string, next?: $shm_projects_goal_run | null ) {
			if( next !== undefined ) return next
			if( !id ) return null
			return this.$.$mol_error_fence(
				()=> $mol_wire_sync( $shm_projects_goal_api ).run( id ),
				()=> null,
				promise => this.$.$mol_fail_hidden( promise ),
			)
		}

		@ $mol_mem
		version_run_events() {
			const events = new this.$.$shm_projects_goal_api_events
			events.id = ()=> this.version_run_id()
			events.notify = ()=> run => this.version_run_receive( run )
			return events
		}

		@ $mol_action
		version_run_receive( run: $shm_projects_goal_run ) {
			this.version_run_data( run.id, run )
			if( run.project ) this.project_record_sync( run.project )
			return run
		}

		project_record_sync( project: $shm_projects_goal_project_summary ) {
			return this.$.$shm_projects_data.summary_sync( project )
		}

		version_release_status() {
			const run = this.version_run_data( this.version_run_id() )
			if( !run ) return ''
			if( run.phase === 'Релиз' ) return 'Релиз выполняется...'
			if( run.phase === 'Ошибка релиза' ) return run.error || 'Релиз не завершён.'
			const version = this.version_selected()?.version
			if( run.deployments.some( deployment =>
				deployment.stage === 'Релиз'
				&& deployment.version === version
				&& ( ( deployment as typeof deployment & { channel_project_id?: string } ).channel_project_id || run.project_id ) === this.project_id()
			) ) return 'Релиз опубликован.'
			return ''
		}

		version_release_status_color() {
			const run = this.version_run_data( this.version_run_id() )
			if( run?.phase === 'Релиз' ) return `${ $shm_theme.active }`
			if( run?.phase === 'Ошибка релиза' ) return `${ $shm_theme.error }`
			return this.version_release_status() ? `${ $shm_theme.success }` : `${ $mol_theme.shade }`
		}

		version_action_rows() {
			const record = this.version_selected()
			if( !record || !record.available ) return []
			const status = this.version_release_status()
			if( this.version_run_data( record.run_id ?? '' )?.phase === 'Релиз' ) return status ? [ this.Version_release_state() ] : []
			const channels = this.project_record().channels
			return [
				...( status ? [ this.Version_release_state() ] : [] ),
				...( channels?.prerelease === record.version ? [] : [ this.Version_prerelease() ] ),
				...( channels?.release === record.version ? [] : [ this.Version_release() ] ),
			]
		}

		version_selected_requirements() {
			return this.version_selected()?.requirements || 'Требования для этой версии не зафиксированы.'
		}

		version_selected_changes() {
			const source = this.version_selected()?.changes
			const items = Array.isArray( source ) ? source : String( source || '' ).split( /\r?\n/ )
			const changes: { title: string; description: string; screenshots: string[] }[] = []
			const artifacts = this.screenshot_artifacts()

			for( const item of items ) {
				if( typeof item !== 'string' ) {
					changes.push({
						title: item.title,
						description: item.description || '',
						screenshots: [ ...( item.screenshots ?? [] ) ],
					})
					continue
				}

				const text = item.replace( /^\s*[-*]\s*/, '' ).trim()
				if( !text ) continue
				const screenshot_line = text.match( /^Скриншоты:\s*(.+)$/i )
				const markdown_images = [ ...text.matchAll( /!\[[^\]]*\]\(([^)]+)\)/g ) ].map( match => match[1] )
				if( screenshot_line || markdown_images.length ) {
					if( !changes.length ) continue
					const candidates = screenshot_line ? screenshot_line[1].split( /[,;]/ ) : markdown_images
					for( const candidate of candidates ) {
						const path = candidate.match( /\]\(([^)]+)\)/ )?.[1] ?? candidate
						const normalized = path.trim().replace( /[`'\"]/g, '' ).replace( /\\/g, '/' ).replace( /^\/+/, '' )
						const artifact = artifacts.find( item => normalized === item.path || normalized.endsWith( `/${ item.path }` ) )
						if( artifact && !changes.at( -1 )!.screenshots.includes( artifact.path ) ) changes.at( -1 )!.screenshots.push( artifact.path )
					}
					continue
				}

				changes.push({ title: text, description: '', screenshots: [] })
			}

			if( changes.length && artifacts.length && !changes.some( change => change.screenshots.length ) ) {
				artifacts.forEach( ( artifact, index )=> {
					changes[ Math.min( index, changes.length - 1 ) ].screenshots.push( artifact.path )
				} )
			}

			return changes.length ? changes : [{ title: 'Список изменений не заполнен', description: '', screenshots: [] }]
		}

		change_rows() {
			return this.version_selected_changes().map( ( _, index )=> this.Change( index ) )
		}

		change_content( index: number ) {
			const change = this.version_selected_changes()[ index ]
			if( !change ) return []
			return [
				this.Change_title( index ),
				...( change.description ? [ this.Change_description( index ) ] : [] ),
				...( change.screenshots.length ? [ this.Change_screenshots( index ) ] : [] ),
			]
		}

		change_title( index: number ) {
			return this.version_selected_changes()[ index ]?.title || ''
		}

		change_description( index: number ) {
			return this.version_selected_changes()[ index ]?.description || ''
		}

		change_screenshot_rows( change: number ) {
			return this.version_selected_changes()[ change ]?.screenshots.map( ( _, screenshot )=> this.Change_screenshot( `${ change }:${ screenshot }` ) ) ?? []
		}

		change_screenshot_uri( key: string ) {
			const [ change, screenshot ] = key.split( ':' ).map( Number )
			const path = this.version_selected_changes()[ change ]?.screenshots[ screenshot ] || ''
			return path ? this.$.$shm_projects_config.resource_url( path ) : ''
		}

		version_artifacts() {
			return this.version_selected()?.artifacts ?? []
		}

		screenshot_artifacts() {
			return this.version_artifacts().filter( artifact => artifact.kind === 'screenshot' )
		}

		other_artifacts_data() {
			return this.version_artifacts().filter( artifact => artifact.kind === 'video' )
		}

		other_artifact_rows() {
			const artifacts = this.other_artifacts_data()
			return artifacts.length ? artifacts.map( ( _, index )=> this.Other_artifact( index ) ) : [ this.No_other_artifacts() ]
		}

		other_artifact_title( index: number ) {
			return this.other_artifacts_data()[ index ]?.title || ''
		}

		other_artifact_uri( index: number ) {
			const path = this.other_artifacts_data()[ index ]?.path || ''
			return path ? this.$.$shm_projects_config.resource_url( path ) : ''
		}

		version_tests_data() {
			return this.version_selected()?.tests ?? []
		}

		version_tests_title() {
			return 'Проверки'
		}

		version_test_rows() {
			const tests = this.version_tests_data()
			return tests.length ? tests.map( ( _, index )=> this.Version_test( index ) ) : []
		}

		version_test_status( index: number ) {
			return this.version_tests_data()[ index ]?.status === 'passed' ? '✓' : '✕'
		}

		version_test_status_color( index: number ) {
			return this.version_tests_data()[ index ]?.status === 'passed'
				? `${ $shm_theme.success }`
				: `${ $shm_theme.error }`
		}

		version_test_title( index: number ) {
			return this.version_tests_data()[ index ]?.title || ''
		}

		version_test_uri( index: number ) {
			const test = this.version_tests_data()[ index ]
			if( !test ) return ''
			const app = this.$.$shm_projects_config.preview_url( 'shm/tests/app' )
			return `${ app }#!test_scenario=${ encodeURIComponent( test.scenario ) }/test_run=${ encodeURIComponent( test.run ) }`
		}

		version_benchmarks_data_all() {
			return this.version_benchmark_source()?.benchmarks ?? []
		}

		version_benchmarks_data() {
			const benchmarks = this.version_benchmarks_data_all()
			if( !this.version_benchmark_file_ids().length ) return benchmarks
			const files = new Set( this.version_benchmark_files_selected() )
			return benchmarks.map( benchmark => ({
				... benchmark,
				values: benchmark.values.filter( value => files.has( value.step.trim() ) ),
			}) ).filter( benchmark => benchmark.values.length )
		}

		private version_benchmark_dimensions() {
			return this.version_benchmark_chart_versions()
				.flatMap( record => record.benchmarks ?? [] )
				.map( benchmark => benchmark.dimension )
				.filter( ( dimension ): dimension is NonNullable< typeof dimension > => Boolean( dimension?.title.trim() ) )
		}

		version_benchmark_dimension_title() {
			const titles = [ ... new Set( this.version_benchmark_dimensions().map( dimension => dimension.title.trim() ) ) ]
			return titles.length === 1 ? titles[0] : 'Наборы данных'
		}

		version_benchmark_dimension_item_title() {
			const titles = [ ... new Set( this.version_benchmark_dimensions().map( dimension => dimension.item_title?.trim() ).filter( Boolean ) ) ]
			if( titles.length === 1 ) return titles[0]!
			return this.version_benchmark_dimension_title() === 'Наборы данных'
				? 'Набор данных'
				: this.version_benchmark_dimension_title()
		}

		no_benchmarks_title() {
			return this.version_benchmarks_data_all().length
				? 'Для выбранных наборов нет результатов'
				: 'Для этой версии бенчмарки не запускались'
		}

		version_benchmarks_panel_rows() {
			return [
				...( this.version_benchmark_file_ids().length ? [ this.Version_benchmark_file_filters() ] : [] ),
				...( this.version_benchmark_line_ids().length ? [ this.Version_benchmark_chart_section() ] : [] ),
				...( this.version_benchmark_info() ? [ this.Version_benchmark_info() ] : [] ),
				...( this.version_benchmark_action_visible() ? [ this.Version_benchmark_run() ] : [] ),
				...( this.version_benchmark_status() ? [ this.Version_benchmark_status() ] : [] ),
				...( this.version_benchmarks_data().length ? [ this.Version_benchmarks() ] : [ this.No_benchmarks() ] ),
				...( this.version_benchmark_legend_rows().length ? [ this.Version_benchmark_legend() ] : [] ),
			]
		}

		version_benchmark_chart_versions() {
			const seen = new Set< string >()
			return this.version_records_display()
				.filter( record => {
					if( !record.benchmarks?.length || seen.has( record.version ) ) return false
					seen.add( record.version )
					return true
				} )
				.toReversed()
		}

		version_benchmark_lines() {
			const lines = new Map< string, $shm_projects_benchmark_line >()
			for( const record of this.version_benchmark_chart_versions() ) {
				for( const benchmark of record.benchmarks ?? [] ) {
					for( const value of benchmark.values ) {
						const id = JSON.stringify([ benchmark.scenario, value.step, value.title, value.unit ])
						if( lines.has( id ) ) continue
						lines.set( id, {
							id,
							title: [ benchmark.title, value.step, value.title ].filter( Boolean ).join( ' · ' ) + ` (${ value.unit })`,
							scenario: benchmark.scenario,
							step: value.step,
							metric: value.title,
							unit: value.unit,
						} )
					}
				}
			}
			return [ ... lines.values() ]
		}

		version_benchmark_line_ids() {
			return this.version_benchmark_lines_for_files().map( line => line.id )
		}

		version_benchmark_line_title( id: string ) {
			return this.version_benchmark_lines().find( line => line.id === id )?.title ?? id
		}

		@ $mol_mem
		override version_benchmark_lines_selected( next?: readonly string[] ) {
			const options = this.version_benchmark_line_ids()
			const key = this.$.$shm_projects_data.state_key( this.project_id(), 'benchmark_chart_lines' )
			if( next !== undefined ) {
				const selected = next.filter( id => options.includes( id ) )
				this.$.$mol_state_local.value< readonly string[] >( key, selected )
				return selected
			}
			const stored = this.$.$mol_state_local.value< readonly string[] >( key )
			return Array.isArray( stored ) ? stored.filter( id => options.includes( id ) ) : options
		}

		version_benchmark_lines_title() {
			return `Линии: ${ this.version_benchmark_lines_selected().length } из ${ this.version_benchmark_line_ids().length }`
		}

		version_benchmark_file_ids() {
			return [ ... new Set( this.version_benchmark_lines().map( line => line.step.trim() ).filter( Boolean ) ) ]
		}

		version_benchmark_file_title( id: string ) {
			return id
		}

		@ $mol_mem
		override version_benchmark_files_selected( next?: readonly string[] ) {
			const options = this.version_benchmark_file_ids()
			const key = this.$.$shm_projects_data.state_key( this.project_id(), 'benchmark_chart_files' )
			if( next !== undefined ) {
				const selected = next.filter( id => options.includes( id ) )
				this.$.$mol_state_local.value< readonly string[] >( key, selected )
				return selected
			}
			const stored = this.$.$mol_state_local.value< readonly string[] >( key )
			return Array.isArray( stored ) ? stored.filter( id => options.includes( id ) ) : options
		}

		version_benchmark_lines_visible() {
			const lines = new Set( this.version_benchmark_lines_selected() )
			return this.version_benchmark_lines_for_files().filter( line => lines.has( line.id ) )
		}

		version_benchmark_lines_for_files() {
			if( !this.version_benchmark_file_ids().length ) return this.version_benchmark_lines()
			const files = new Set( this.version_benchmark_files_selected() )
			return this.version_benchmark_lines().filter( line => files.has( line.step.trim() ) )
		}

		version_benchmark_legend_operations() {
			const operations: string[] = []
			for( const line of this.version_benchmark_lines_visible() ) {
				const operation = this.version_benchmark_value_parts( line.metric ).operation
				if( !operations.includes( operation ) ) operations.push( operation )
			}
			return operations
		}

		version_benchmark_legend_rows() {
			return this.version_benchmark_legend_operations().map( operation => this.Version_benchmark_legend_item( operation ) )
		}

		version_benchmark_legend_item_title( operation: string ) {
			return operation
		}

		private version_benchmark_legend_fallback( operation: string ) {
			const known: Record< string, string > = {
				'Открытие файла': 'Выбор локального файла, сохранение в OPFS, открытие DuckDB и готовность сводки, данных графика и первой страницы постов.',
				'Открытие файла из OPFS': 'Перезагрузка приложения, чтение сохранённого файла из OPFS, открытие DuckDB и готовность сводки, данных графика и первой страницы постов.',
				'Отрисовка графика': 'Смена группировки, выполнение запроса временного ряда и обновление DOM графика; посты и сводка не входят.',
				'Отрисовка постов': 'Переключение страницы, выполнение DuckDB-запроса и обновление DOM ленты постов; график и сводка не входят.',
				'Отрисовка первой страницы постов': 'Переключение страницы, выполнение DuckDB-запроса и обновление DOM ленты постов; график и сводка не входят.',
				'Сортировка постов': 'Смена сортировки, выполнение запроса первой страницы и обновление DOM ленты постов.',
				'Поиск по тексту': 'FTS-поиск и пересчёт сводки, данных графика и первой страницы постов.',
				'Фильтр по источнику': 'Применение фильтра источника и пересчёт сводки, данных графика и первой страницы постов.',
				'Фильтр по тегу': 'Применение фильтра тега и пересчёт сводки, данных графика и первой страницы постов.',
				'Фильтр по типу': 'Применение фильтра типа и пересчёт сводки, данных графика и первой страницы постов.',
				'Фильтр по дате': 'Применение фильтра даты и пересчёт сводки, данных графика и первой страницы постов.',
			}
			return known[ operation ] ?? 'Состав замера указан в данных бенчмарка.'
		}

		version_benchmark_legend_item_description( operation: string ) {
			const files = new Set( this.version_benchmark_files_selected() )
			const filter_files = this.version_benchmark_file_ids().length > 0
			const notes = this.version_benchmark_chart_versions().flatMap( record => record.benchmarks ?? [] )
				.flatMap( benchmark => benchmark.values )
				.filter( value => ( !filter_files || files.has( value.step.trim() ) )
					&& this.version_benchmark_value_parts( value.title ).operation === operation )
				.map( value => value.note?.trim() ?? '' )
				.filter( Boolean )
			const detailed = notes.find( note => !/^\d+\s+повтор(?:а|ов)?\.?$/i.test( note ) )
			if( detailed ) return detailed
			const repetitions = notes.find( note => /^\d+\s+повтор(?:а|ов)?\.?$/i.test( note ) )
			return [ this.version_benchmark_legend_fallback( operation ), repetitions ].filter( Boolean ).join( ' ' )
		}

		private version_benchmark_line_value( record: $shm_projects_data_version, line: $shm_projects_benchmark_line ) {
			return record.benchmarks?.find( benchmark => benchmark.scenario === line.scenario )?.values.find( value =>
				value.step === line.step && value.title === line.metric && value.unit === line.unit
			)?.value ?? null
		}

		version_benchmark_chart_option() {
			const versions = this.version_benchmark_chart_versions()
			const lines = this.version_benchmark_lines_visible()
			const units = [ ... new Set( lines.map( line => line.unit ) ) ]
			return {
				tooltip: { trigger: 'axis' },
				grid: {
					left: units.length > 2 ? 72 : 56,
					right: units.length > 1 ? 72 : 24,
					top: 28,
					bottom: 36,
					containLabel: true,
				},
				xAxis: {
					type: 'category',
					boundaryGap: false,
					data: versions.map( record => `v${ record.version }` ),
				},
				yAxis: units.length ? units.map( ( unit, index )=> ({
					type: 'value',
					name: unit,
					position: index % 2 ? 'right' : 'left',
					offset: Math.floor( index / 2 ) * 48,
					scale: true,
				}) ) : [{ type: 'value', scale: true }],
				series: lines.map( line => ({
					id: line.id,
					name: line.title,
					type: 'line',
					showSymbol: true,
					connectNulls: true,
					yAxisIndex: Math.max( 0, units.indexOf( line.unit ) ),
					data: versions.map( record => this.version_benchmark_line_value( record, line ) ),
				})),
			}
		}

		version_benchmark_source() {
			const selected = this.version_selected()
			if( !selected ) return null
			if( this.version_benchmark_record_has_values( selected ) ) return selected
			const records = this.version_records_display()
			return records.find( record => record.version === selected.version && this.version_benchmark_record_has_values( record ) )
				?? records.slice( records.indexOf( selected ) + 1 ).find( record => this.version_benchmark_record_has_values( record ) )
				?? null
		}

		private version_benchmark_record_has_values( record: $shm_projects_data_version ) {
			return Boolean( record.benchmarks?.some( benchmark => benchmark.values.length ) )
		}

		version_benchmark_previous() {
			const source = this.version_benchmark_source()
			if( !source ) return null
			const records = this.version_records_display()
			return records.slice( records.indexOf( source ) + 1 )
				.find( record => record.version !== source.version && this.version_benchmark_record_has_values( record ) ) ?? null
		}

		version_benchmark_info() {
			const selected = this.version_selected()
			const source = this.version_benchmark_source()
			if( !selected || !source ) return ''
			const inherited = source.version !== selected.version
			const previous = this.version_benchmark_previous()
			return [
				...( inherited ? [ `Для v${ selected.version } показаны последние результаты v${ source.version }.` ] : [] ),
				...( previous ? [ `Изменения рассчитаны относительно v${ previous.version }.` ] : [] ),
			].join( ' ' )
		}

		version_benchmark_action_visible() {
			const selected = this.version_selected()
			return selected?.version === this.project_record().version
				&& Boolean( this.project_record().benchmarks?.commands.length )
				&& !selected.benchmarks?.length
		}

		version_benchmark_status() {
			const state = this.version_session_run()?.benchmark
			if( !state || state.version !== this.version_selected()?.version ) return ''
			if( state.status === 'running' ) return 'Бенчмарки выполняются…'
			if( state.status === 'failed' ) return `Ошибка бенчмарков: ${ state.error || 'прогон не завершён' }`
			return ''
		}

		version_benchmark_status_color() {
			const status = this.version_session_run()?.benchmark?.status
			if( status === 'running' ) return `${ $shm_theme.active }`
			if( status === 'failed' ) return `${ $shm_theme.error }`
			return `${ $mol_theme.shade }`
		}

		@ $mol_action
		version_benchmark_run( next?: Event ) {
			if( next === undefined ) return null
			const version = this.version_selected()?.version
			if( !version ) return next
			this.version_run_receive( $mol_wire_sync( $shm_projects_goal_api ).benchmark_run( this.project_id(), version ) )
			return next
		}

		version_benchmark_rows() {
			return this.version_benchmarks_data().map( ( _, index )=> this.Version_benchmark( index ) )
		}

		version_benchmark_metrics() {
			const metrics: string[] = []
			for( const benchmark of this.version_benchmarks_data() ) {
				for( const value of benchmark.values ) {
					const metric = this.version_benchmark_value_parts( value.title ).metric
					if( !metrics.includes( metric ) ) metrics.push( metric )
				}
			}
			return metrics
		}

		version_benchmark_metric_filter_title( metric: string ) {
			return metric.slice( 0, 1 ).toLocaleUpperCase( 'ru' ) + metric.slice( 1 )
		}

		version_benchmark_title( index: number ) {
			const benchmark = this.version_benchmarks_data()[ index ]
			return benchmark ? `${ benchmark.status === 'passed' ? '✓' : '✕' } ${ benchmark.title }` : ''
		}

		private version_benchmark_value_parts( title: string ) {
			const separator = title.lastIndexOf( ' · ' )
			if( separator < 0 ) return { operation: title, metric: 'Значение' }
			return {
				operation: title.slice( 0, separator ),
				metric: title.slice( separator + 3 ),
			}
		}

		version_benchmark_table_data( index: number ) {
			const rows: $shm_projects_benchmark_row[] = []
			for( const value of this.version_benchmarks_data()[ index ]?.values ?? [] ) {
				const { operation, metric } = this.version_benchmark_value_parts( value.title )
				let row = rows.find( item => item.step === value.step && item.operation === operation )
				if( !row ) {
					row = { step: value.step, operation, values: new Map() }
					rows.push( row )
				}
				row.values.set( metric, value )
			}
			return rows
		}

		version_benchmark_table_header_cells( index: number ) {
			return [ 'step', 'operation', ... this.version_benchmark_metrics().map( metric => `metric:${ metric }` ) ].map(
				column => this.Version_benchmark_table_header_cell( JSON.stringify( [ index, column ] ) ),
			)
		}

		version_benchmark_table_header_cell_text( key: string ) {
			const [ , column ] = JSON.parse( key ) as [ number, string ]
			if( column === 'step' ) return this.version_benchmark_dimension_item_title()
			if( column === 'operation' ) return 'Операция'
			return this.version_benchmark_metric_filter_title( column.slice( 'metric:'.length ) )
		}

		version_benchmark_table_body_rows( index: number ) {
			return this.version_benchmark_table_data( index ).map( ( _, row ) =>
				this.Version_benchmark_table_row( JSON.stringify( [ index, row ] ) ),
			)
		}

		version_benchmark_table_row_cells( key: string ) {
			const [ index, row ] = JSON.parse( key ) as [ number, number ]
			return [ 'step', 'operation', ... this.version_benchmark_metrics().map( metric => `metric:${ metric }` ) ].map(
				column => this.Version_benchmark_table_cell( JSON.stringify( [ index, row, column ] ) ),
			)
		}

		version_benchmark_table_cell_text( key: string ) {
			const [ index, row_index, column ] = JSON.parse( key ) as [ number, number, string ]
			const row = this.version_benchmark_table_data( index )[ row_index ]
			if( !row ) return ''
			if( column === 'step' ) return row.step || '—'
			if( column === 'operation' ) return row.operation
			const value = row.values.get( column.slice( 'metric:'.length ) )
			if( !value ) return '—'
			return `${ Math.round( value.value * 100 ) / 100 } ${ value.unit }`
		}

		version_benchmark_table_cell_difference_text( key: string ) {
			const [ index, row_index, column ] = JSON.parse( key ) as [ number, number, string ]
			if( !column.startsWith( 'metric:' ) ) return ''
			const value = this.version_benchmark_table_data( index )[ row_index ]?.values.get( column.slice( 'metric:'.length ) )
			if( !value ) return ''
			const difference = this.version_benchmark_difference( index, value )
			if( !difference ) return ''
			return ` (${ difference.absolute > 0 ? '+' : '' }${ difference.absolute } ${ value.unit })`
		}

		version_benchmark_table_cell_change( key: string ) {
			const [ index, row_index, column ] = JSON.parse( key ) as [ number, number, string ]
			if( !column.startsWith( 'metric:' ) ) return ''
			const value = this.version_benchmark_table_data( index )[ row_index ]?.values.get( column.slice( 'metric:'.length ) )
			if( !value ) return ''
			return this.version_benchmark_difference( index, value )?.change ?? ''
		}

		private version_benchmark_difference( index: number, value: $shm_projects_benchmark_value ) {
			const current = this.version_benchmarks_data()[ index ]
			const previous = this.version_benchmark_previous()?.benchmarks?.find( benchmark => benchmark.scenario === current?.scenario )
				?.values.find( known => known.step === value.step && known.title === value.title && known.unit === value.unit )
			if( !previous || previous.value === value.value ) return null
			const raw = value.value - previous.value
			const absolute = Math.round( raw * 100 ) / 100
			const improved = value.better === 'higher' ? raw > 0 : raw < 0
			return { absolute, change: improved ? 'better' : 'worse' }
		}

		@ $mol_action
		override release_make( next?: Event ) {
			if( next === undefined || !this.release_allowed() ) return null
			const record = this.version_selected()
			if( !record ) return next
			const run = record.available
				? $mol_wire_sync( $shm_projects_goal_api ).version_activate( this.project_id(), record.version, 'release' )
				: $mol_wire_sync( $shm_projects_goal_api ).deploy( record.run_id ?? '', 'release', '' )
			this.version_run_receive( run )
			const deployment = run.deployments.find( deployment => deployment.stage === 'Релиз' )
			if( deployment ) this.$.$mol_state_arg.value( 'project_version', deployment.record.id )
			return next
		}

		@ $mol_action
		override prerelease_activate( next?: Event ) {
			if( next === undefined || !this.prerelease_allowed() ) return null
			const record = this.version_selected()
			if( !record?.available ) return next
			const run = $mol_wire_sync( $shm_projects_goal_api ).version_activate( this.project_id(), record.version, 'prerelease' )
			this.version_run_receive( run )
			return next
		}

		prerelease_allowed() { return this.$.$shm_projects_access_prerelease( this.project_id() ) }
		release_allowed() { return this.$.$shm_projects_access_release( this.project_id() ) }

	}

}
